<template>
    <div v-if="canEdit" class="edit-sets" ref="container">
        <div v-if="!dialogMode" class="es-heading">
            {{ $t('common.edit') }} {{ $t('common.config') }}
        </div>

        <template v-if="isCircuit">
            <div class="es-label">{{ $t('exercises.reps') }}</div>
            <kimsa-text-field :value="circuitValue" @change="circuitValue = $event" dense force-value centered-value
                no-elevation />
        </template>

        <div v-else class="es-grid" :style="gridStyle">
            <div class="es-head">{{ $tc('exercises.sets', 1) }}</div>
            <div v-if="hasReps" class="es-head">{{ $t('exercises.reps') }}</div>
            <div v-if="hasWeight" class="es-head">{{ $t('exercises.weight') }}</div>
            <div v-if="hasTime" class="es-head">{{ $t('exercises.time') }}</div>
            <div v-if="hasIntensity" class="es-head">{{ intensityLabel }}</div>
            <div class="es-head"></div>

            <template v-for="(set, i) in dataSet.sets">
                <div :key="`n-${i}`" class="es-index">{{ i + 1 }}</div>
                <div :key="`r-${i}`" v-if="hasReps">
                    <kimsa-text-field :value="getValue(i, 'reps')" @change="setValue($event, i, 'reps')" dense
                        force-value centered-value no-elevation />
                </div>
                <div :key="`w-${i}`" v-if="hasWeight">
                    <kimsa-text-field :value="getValue(i, 'weight')" @change="setValue($event, i, 'weight')" dense
                        force-value centered-value no-elevation :placeholder="getValuePlaceholder(i)" />
                </div>
                <div :key="`t-${i}`" v-if="hasTime">
                    <kimsa-text-field :value="getValue(i, 'time')" @change="setValue($event, i, 'time')" dense
                        force-value centered-value no-elevation />
                </div>
                <div :key="`i-${i}`" v-if="hasIntensity">
                    <kimsa-text-field :value="getValue(i, intensityType)" @change="setValue($event, i, intensityType)"
                        dense force-value centered-value no-elevation />
                </div>
                <div :key="`d-${i}`" class="es-delete">
                    <button-tooltip :tooltip="$t('common.delete')">
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn icon small v-on="on" v-bind="attrs" @click="deleteSet(i)"
                                :disabled="loading || dataSet.sets.length <= 1">
                                <v-icon size="18" color="error">$delete</v-icon>
                            </v-btn>
                        </template>
                    </button-tooltip>
                </div>
            </template>
        </div>

        <div class="es-actions">
            <v-btn v-if="!isCircuit" depressed height="42" @click="addSet" :disabled="loading"
                class="normal-btn es-btn es-btn-add">
                {{ $t('exercises.add_set') }}
            </v-btn>
            <v-btn depressed height="42" class="normal-btn es-btn es-btn-save black--text" @click="save"
                :loading="loading">
                {{ $t('common.save') }}
            </v-btn>
        </div>
    </div>
</template>

<script>
import {db} from "@/utils/Firebase";
import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import ButtonTooltip from "@/components/common/ButtonTooltip";
import { clearBlocksAssignedRoutine, SET_NAMES } from "../../utils/Exercises";

const INDEX_COLUMN_WIDTH = '40px'

const INTENSITY_SHORT_NAMES = [
    ...SET_NAMES.RPE,
    ...SET_NAMES.INTENSITY.filter(name => name !== 'short_time' && !SET_NAMES.RPE.includes(name))
]

export default {
    name: "EditSets",
    components: {KimsaTextField, ButtonTooltip},
    props: {
        assignedRoutineId: {
            type: String,
            default: null,
        },
        exerciseId: {
            type: String,
            default: null,
        },
        config: {
            type: Object,
            default: null,
        },
        block: {
            type: [Number, String],
            default: null,
        },
        blocks: {
            type: Array,
            default: null,
        },
        category: {
            type: String,
            default: null,
        },
        isCircuit: {
            type: Boolean,
            default: false,
        },
        routine: {
            type: Object,
            default: null,
        },
        dialogMode: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            loading: false,
            dataSet: [],
            circuitValue: 0,
            previousWeight: []
        }
    },
    computed: {
        hasReps(vm = this) {
            return !!vm.config?.sets?.[0]?.data?.find(data => data.short_name === 'short_reps')
        },
        hasWeight(vm = this) {
            return !!vm.config?.sets?.[0]?.data?.find(data => data.short_name === 'short_weight')
        },
        hasTime(vm = this) {
            return !!vm.config?.sets?.[0]?.data?.find(data => data.short_name === 'short_time')
        },
        pageHeight() {
            return document.body.scrollHeight
        },
        intensityShortName(vm = this) {
            const data = vm.config?.sets?.[0]?.data || []
            return data.find(item => INTENSITY_SHORT_NAMES.includes(item.short_name))?.short_name || null
        },
        intensityType(vm = this) {
            return vm.intensityShortName ? vm.intensityShortName.replace('short_', '') : null
        },
        intensityLabel(vm = this) {
            return vm.intensityType ? vm.$t(`exercises.${vm.intensityType}`) : ''
        },
        hasIntensity(vm = this) {
            return !!vm.intensityShortName
        },
        isIntensityCategory(vm = this) {
            return vm.categoryKey(vm.category) === vm.CATEGORY.SPEED || vm.categoryKey(vm.category) === vm.CATEGORY.RESISTANCE
        },
        canEdit(vm = this) {
            return vm.hasReps || vm.hasWeight || vm.hasTime || vm.isCircuit || vm.hasIntensity || vm.isIntensityCategory
        },
        circuitLabel(vm = this) {
            if (!vm.isCircuit) return ''
            return vm.$t(`exercises.${vm.config.type}`)
        },
        gridStyle(vm = this) {
            let qty = 0
            if (vm.hasReps) qty++
            if (vm.hasWeight) qty++
            if (vm.hasTime) qty++
            if (vm.hasIntensity) qty++
            return { gridTemplateColumns: `${INDEX_COLUMN_WIDTH} repeat(${qty}, minmax(0, 1fr)) 32px` }
        }
    },
    mounted() {
        const vm = this
        vm.loadDataSet()
        vm.loadPreviousWeek()
    },
    methods: {
        async loadPreviousWeek() {
            const vm = this
            if (!vm.routine || !vm.routine.studentId || !vm.routine.rawDate?.full) return
            let previousWeekRoutine = await db.collection('assignedRoutines')
                .where('studentId', '==', vm.routine.studentId)
                .where('rawDate.full', '==', vm.$moment(vm.routine.rawDate.full, 'YYYY-MM-DD').subtract(7, 'days').format('YYYY-MM-DD'))
                .where('routineNumber', '==', vm.routine.routineNumber || 1)
                .limit(1)
                .get()

            let previousWeight = []

            if (!previousWeekRoutine.empty) {
                let previousData = previousWeekRoutine.docs[0].data()
                let previousBlocks = previousData.blocks
                let exercise = previousBlocks?.[vm.block - 1]?.exercises?.find(ex => ex.id === vm.exerciseId)
                if (exercise && exercise.config) {
                    exercise?.config?.sets?.forEach((set, i) => {
                        let currentWeight = set?.data?.find(d => d.short_name === 'short_weight')?.value || null
                        if (currentWeight) previousWeight[i] = currentWeight
                    })
                }
            }

            vm.previousWeight = previousWeight
        },
        async loadDataSet() {
            const vm = this
            if (!vm.config) return
            if (vm.isCircuit) {
                vm.circuitValue = vm.config.value
            }
            if (!vm.hasWeight && vm.categoryKey(vm.category) === vm.CATEGORY.STRONG) {
                let payload = {
                    short_name: 'short_weight',
                    value: null,
                }
                vm.config?.sets?.forEach((set) => {
                    set.data.push({...payload})
                })
            }
            vm.dataSet = JSON.parse(JSON.stringify(vm.config))
        },
        getValuePlaceholder(index) {
            const vm = this;
            return vm.previousWeight?.[index] || '0'
        },
        getValue(index, type) {
            const vm = this
            if (!vm.dataSet.sets) return null
            return vm.dataSet?.sets[index]?.data?.find(data => data.short_name === `short_${type}`)?.value || null
        },
        setValue(value, index, type) {
            const vm = this
            let valueIndex = vm.dataSet.sets[index].data.findIndex(data => data.short_name === `short_${type}`)
            if (valueIndex >= 0) vm.dataSet.sets[index].data[valueIndex].value = value || 0
        },
        deleteSet(index) {
            const vm = this
            vm.dataSet.sets.splice(index, 1);
        },
        addSet() {
            const vm = this
            const lastSet = vm.dataSet.sets?.[vm.dataSet.sets.length - 1]
            if (!lastSet) return
            let dataSet = JSON.parse(JSON.stringify(vm.dataSet))
            dataSet.sets.push(lastSet)
            vm.dataSet = dataSet
            if(!vm.dialogMode) vm.$vuetify.goTo(vm.pageHeight)
        },
        async save() {
            const vm = this
            vm.loading = true
            try {
                let blocks = clearBlocksAssignedRoutine(vm.blocks, false)
                let exerciseIndex = blocks[vm.block - 1].exercises.findIndex(ex => ex.id === vm.exerciseId)
                if (vm.isCircuit) {
                    blocks[vm.block - 1].exercises[exerciseIndex].config.value = vm.circuitValue
                } else {
                    blocks[vm.block - 1].exercises[exerciseIndex].config = JSON.parse(JSON.stringify(vm.dataSet))
                }
                if (!await vm.saveAssignedRoutine(vm.assignedRoutineId, {blocks: blocks})) return
                vm.toast(vm.$t('exercises.updated_sets'))
                vm.$emit('updatedSet', blocks[vm.block - 1].exercises[exerciseIndex].config)
            } catch (e) {
                console.error('EditSets.save error', e)
                vm.toast(e && e.message || 'Error', 'error')
            } finally {
                vm.loading = false
            }
        }
    },
}
</script>

<style scoped>

.edit-sets {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.es-heading {
    font-size: var(--fs-body);
    font-weight: 600;
    color: var(--v-txtPrimary-base);
    text-align: center;
    padding-bottom: 12px;
    border-bottom: 1px solid var(--v-outline-base);
}

.es-grid {
    display: grid;
    align-items: center;
    column-gap: 8px;
    row-gap: 4px;
}

.es-head,
.es-label {
    text-align: center;
    font-size: 11px;
    font-weight: 700;
    color: var(--v-txtMuted-base);
    text-transform: uppercase;
    letter-spacing: 0.06em;
    white-space: nowrap;
}

.es-index {
    text-align: center;
    font-size: 13px;
    font-weight: 600;
    color: var(--v-txtSecondary-base);
}

.es-delete {
    text-align: center;
}

.edit-sets ::v-deep .v-text-field--solo > .v-input__control > .v-input__slot {
    border-radius: 7px;
}

.es-actions {
    display: flex;
    gap: 8px;
}

.es-btn {
    flex: 1 1 0;
}

.es-btn-add {
    background: rgba(51, 153, 153, 0.12) !important;
    border: 1px solid rgba(51, 153, 153, 0.3);
    color: var(--v-accent-base) !important;
}

.es-btn-save {
    background: var(--brand-gradient) !important;
}

</style>